<script setup lang="ts">
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import TrackingButton from '../TrackingButton.vue';
import PackageInvoice from './PackageInvoice.vue';
import BusSeatsSmall from './BusSeatsSmall.vue';
import HotelInfo from './HotelInfo.vue';
import ExtraTerms from './package-info/ExtraTerms.vue';
import CancellationTerms from './CancellationTerms.vue';
import PackageInfo from './package-info/PackageInfo.vue';
import TrainMidStations from './TrainMidStations.vue';
import ReserveBill from '@components/ui/hotelPage&reserve&view/reserve-bill/ReserveBill.vue';
import FooterButtons from '@components/util/FooterButtons.vue';
import type TripPackage from '@itinerary/trip/TripPackage';
import {
    TripInfoComponent,
    type TripPackageDetails,
} from '@itinerary/trip/types';
import { type InvoiceEntry } from '@store/invoice';
import { PaxTypeTitles } from '@itinerary/trip/TripModuleTypes';
import { type MrBillItemsGroup } from '@components/ui/hotelPage&reserve&view/reserve-bill/types';

interface IProps {
    tripPackage: TripPackage;
    hideInvoice?: boolean;
}
const { tripPackage, hideInvoice } = defineProps<IProps>();
const emit = defineEmits<{
    reserve: [];
    hideDetails: [];
}>();
const detailsComponentsMap: Record<TripInfoComponent, any> = {
    [TripInfoComponent.BusSeatsSmall]: BusSeatsSmall,
    [TripInfoComponent.PackageInfo]: PackageInfo,
    [TripInfoComponent.CancellationTerms]: CancellationTerms,
    [TripInfoComponent.TrainMidStations]: TrainMidStations,
    [TripInfoComponent.HotelInfo]: HotelInfo,
    [TripInfoComponent.ExtraTerms]: ExtraTerms,
};
export type FareItem = {
    title: string;
    count: number;
    value: string;
    nValue: number;
};
const itineraryStore = useItineraryStore();
const currentSearchForm = useCurrentSearchForm();
const currentTabIndex = ref(0);
const packageInvoices = ref<InvoiceEntry[]>([]);
const isLoadingInvoice = ref(false);
const price = computed(() => tripPackage.defaultPrice);

const invoiceItems = computed<MrBillItemsGroup>(() => ({
    items: fares.value.slice(0, -1).map(fare => ({
        unitPrice: fare.nValue,
        count: fare.count,
        title: fare.title,
    })),
}));
const fares = computed(() => {
    const list: FareItem[] = packageInvoices.value.map(invoice => ({
        title: invoice.title || PaxTypeTitles[invoice.paxType],
        count: invoice.count,
        value: formatMoney(invoice.price, {
            includeUnit: true,
        }),
        nValue: invoice.price,
    }));
    const total: number = list.reduce((t, r) => r.nValue * r.count + t, 0);
    list.push({
        title: 'مجموع',
        value: formatMoney(total, {
            includeUnit: true,
        }),
        count: 0,
        nValue: total,
    });
    return list;
});
const mobileHideDetail = () => emit('hideDetails');
const tabs = computed<TripPackageDetails[]>(() => {
    if (!tripPackage.reservable) {
        return [
            {
                title: 'اطلاعات سفر',
                component: TripInfoComponent.PackageInfo,
            },
        ];
    }
    return tripPackage.tripPackageDetails;
});

const btnColorVariant = computed(() =>
    autoReserveEnabled.value ? 'secondary' : 'primary'
);
const mobileHeaderTitle = computed(
    () =>
        `${tripPackage.firstTrip.origin} به ${
            tripPackage.segments[0].trips[
                tripPackage.segments[0].trips.length - 1
            ].destination
        } ${tripPackage.segments[0].trips[0].dateString.replace(/^\S+\s+/, '')}`
);
const autoReserveEnabled = computed(
    () => itineraryStore.hasAutoReserve && tripPackage.autoReserveEnabled
);
const fullHeight = ref('100vh');
onMounted(async () => {
    fullHeight.value = window?.innerHeight
        ? `${window.innerHeight}px`
        : '100vh';
    isLoadingInvoice.value = true;
    try {
        packageInvoices.value = await tripPackage.loadPreInvoice({
            adultCount: currentSearchForm.value.adultCount,
            childCount: currentSearchForm.value.childCount,
            infantCount: currentSearchForm.value.infantCount,
        });
    } catch (e) {
        reportException(e);
    } finally {
        isLoadingInvoice.value = false;
    }
});
</script>

<template>
    <div id="MrPPackageDetails" class="package-details-container">
        <div class="mobile-view-backdrop mobile-only" />
        <header class="mobile-header">
            <mr-button
                :icon-button="{ icon: faArrowRight, color: 'white' }"
                variant="transparent"
                @click="mobileHideDetail"
            />
            <div class="title">
                {{ mobileHeaderTitle }}
            </div>
            <div class="header-actions">
                <TrackingButton :trip-package white mobile-hide-tooltip />
                <TrackingButton
                    v-if="tripPackage.canPriceAutoReserve"
                    :trip-package
                    white
                    mobile-hide-tooltip
                    type="reserve"
                />
            </div>
        </header>
        <section class="details-section">
            <header class="tabs-header">
                <button
                    v-for="(tab, index) in tabs"
                    :key="tab.title"
                    class="tab"
                    :class="{ selected: currentTabIndex === index }"
                    @click="currentTabIndex = index"
                >
                    {{ tab.title }}
                </button>
            </header>
            <mr-divider :thickness="1" />
            <div class="tab-contents-wrapper">
                <template v-for="(tab, index) in tabs" :key="tab.component">
                    <transition>
                        <keep-alive class="tab-content-container">
                            <component
                                :is="detailsComponentsMap[tab.component]"
                                v-if="currentTabIndex === index"
                                class="tab-content-container"
                                :trip-package
                            />
                        </keep-alive>
                    </transition>
                </template>
            </div>
        </section>
        <mr-divider class="desktop-only" vertical :thickness="1" />
        <section class="pricing-section">
            <template v-if="tripPackage.reservable">
                <mr-dots-loading v-if="isLoadingInvoice" />
                <PackageInvoice v-else-if="price && !hideInvoice" :fares />
            </template>
            <div class="spacer" />
            <TrackingButton class="tracking-button" :trip-package in-details />
            <TrackingButton
                v-if="tripPackage.canPriceAutoReserve"
                class="tracking-button"
                :trip-package
                in-details
                type="reserve"
            />
            <mr-button
                class="reserve-button"
                full-width
                @click="emit('reserve')"
            >
                ادامه
            </mr-button>
        </section>
        <FooterButtons
            v-if="!hideInvoice"
            class="mobile-invoice"
            fixed
            has-cancel
            mobile-only
            :proceed-button-name="
                autoReserveEnabled ? 'موجود شد رزرو کن!' : 'ادامه'
            "
            :proceed-button-color="btnColorVariant"
            @click:cancel="mobileHideDetail"
            @click:proceed="emit('reserve')"
        >
            <template v-if="!autoReserveEnabled" #prepend>
                <ReserveBill
                    :items="[invoiceItems]"
                    hide-credit
                    initial-state="closed"
                />
            </template>
        </FooterButtons>
    </div>
</template>

<style lang="scss" scoped>
.package-details-container {
    display: flex;
    @include mobile-only() {
        width: 100vw;
        direction: rtl;
        flex-direction: column;
        background: white;
        height: v-bind(fullHeight);
        .mobile-view-backdrop {
            position: fixed;
            z-index: -1;
            inset: 0;
            min-height: 100vh;
            background-color: white;
        }
    }
}

.mobile-header {
    @include desktop-only() {
        display: none;
    }
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 60px;
    padding: 8px;
    background: usePalette();
    .title {
        @include tg-l($w7, white);
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }
    @include mobile-only {
        .header-actions {
            display: flex;
        }
    }
}

.details-section {
    flex: 1 1 auto;
    @include v-transition-horizontal-paging();
    display: flex;
    flex-direction: column;
    overflow: hidden;
    @include mobile-only() {
        max-height: calc(100% - 172px);
    }
    .tabs-header {
        display: flex;
        padding-right: 20px;
        @include mobile-only() {
            @include no-scrollbar;
            padding: 0 16px 0 0;
            overflow-x: scroll;
        }
        .tab {
            all: unset;
            @include tg-m($w5, usePalette($p-cool-gray, 600));
            padding: 7px 16px;
            margin-left: 4px;
            border-bottom-width: 2px;
            border-bottom-color: transparent;
            border-bottom-style: solid;
            cursor: pointer;
            white-space: nowrap;
            &.selected {
                color: usePalette();
                border-bottom-color: usePalette();
            }
        }
    }

    .tab-contents-wrapper {
        @include no-scrollbar();
        display: flex;
        width: 100%;
        overflow-x: hidden;
        background: usePalette($p-gray, 100);
        @include mobile-only() {
            flex: 1;
            background: white;
            overflow-y: scroll;
        }
        .tab-content-container {
            flex: 1;
            min-width: 100%;
        }
    }
}

.pricing-section {
    flex: 0 0 230px;
    display: flex;
    flex-direction: column;
    width: 230px;
    padding: 8px 20px 20px 20px;
    gap: 12px;
    .reserve-button {
        display: none;
    }
    .spacer {
        flex-grow: 1;
    }
    .tracking-button {
        width: 100%;
        align-self: center;
        flex-shrink: 0;
    }
    @include mobile-only {
        display: none;
        @include shadow-level1();
        flex: 0;
        align-self: stretch;
        width: 100%;
        padding: 8px;
        .reserve-button {
            display: flex;
        }
    }
}

.mobile-invoice {
    position: fixed;
    inset: auto 0 0 0;
}
</style>
